'use client'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { Discussion } from '@web/gen/types'
import { clubhousesGetDiscussionQueryFetcher } from '@web/gen/clubhouses'

interface UseDiscussionOptions {
  enabled?: boolean
}

export function useDiscussion(discussionSlug: string, options?: UseDiscussionOptions) {
  const queryKey = ['discussion', discussionSlug] as const
  const queryClient = useQueryClient()
  const enabled = options?.enabled ?? true

  const queryFn = React.useCallback(async () => {
    const response = await clubhousesGetDiscussionQueryFetcher({
      input: { discussionSlug },
    })()

    return response?.clubhousesGetDiscussion?.discussion as Discussion
  }, [discussionSlug])

  const { data: discussionData, isPending } = useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const refreshDiscussion = async () => {
    try {
      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn,
      })

      return result
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing discussion:', error)
      throw error
    }
  }

  return {
    discussionData: discussionData as Discussion | undefined,
    refreshDiscussion,
    isLoading: isPending,
  }
}
